// Staff Board Page - Beautiful Cards with Rank Badges
import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Users, Plus, Search, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

import { usersApi, wacheApi, tagsApi, permissionsApi, dienstApi, kategorienApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { canManageAllDepartments, canManageRole, canManageUserAction } from '../lib/rolePermissions';
import { FahrzeugFreigabenEditor } from '../components/mitarbeiter/FahrzeugFreigabenEditor';
import { cn, roleLabels } from '../lib/utils';
import { compareByDienstgrad } from '../lib/dienstgrade';
import UserDetailDialog from '../components/mitarbeiter/UserDetailDialog';
import { dienstgradOptions, qualifikationOptions, rolleOptions } from '../components/mitarbeiter/employeeOptions';
import StaffCard from '../components/mitarbeiter/StaffCard';
import { suggestLoginname } from '../lib/loginname';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// Mitarbeiter hinzufügen Dialog
function AddUserDialog({ open, onOpenChange, abteilungen, onSuccess, currentUserRolle, currentUserAbteilungId, canCreateAllDepartments, canAssignBenutzergruppen, availableKategorien = [], availableTags = [] }) {
  const [isLoading, setIsLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [loginnameEdited, setLoginnameEdited] = useState(false);
  const abtEditable = canCreateAllDepartments;
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    loginname: '',
    email: '',
    telefon: '',
    dienstgrad: '',
    qualifikation: '',
    abteilung_id: '',
    rolle: 'MITARBEITER',
    kategorien_ids: [],
    tag_ids: [],
  });

  const updateName = (field, value) => {
    setFormData(previous => {
      const next = { ...previous, [field]: value };
      if (!loginnameEdited) next.loginname = suggestLoginname(next.vorname, next.nachname);
      return next;
    });
  };

  useEffect(() => {
    // Bei ADMIN/WAL wird die Abteilung vom Backend auf die eigene gesetzt
    // → im UI direkt die eigene Abteilung anzeigen und disabled.
    if (!abtEditable && currentUserAbteilungId && !formData.abteilung_id) {
      setFormData(prev => ({ ...prev, abteilung_id: currentUserAbteilungId }));
    } else if (abteilungen.length > 0 && !formData.abteilung_id) {
      setFormData(prev => ({ ...prev, abteilung_id: abteilungen[0].id }));
    }
  }, [abteilungen, abtEditable, currentUserAbteilungId]);

  useEffect(() => {
    if (!open) {
      setCreatedUser(null);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Abteilung ist nur für reguläre Rollen erforderlich
    const isLeitungsRolle = formData.rolle === 'WACHLEITUNG' || formData.rolle === 'WACHLEITUNG_VERTRETUNG';
    
    if (!formData.vorname || !formData.nachname || !formData.loginname) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    if (!isLeitungsRolle && !formData.abteilung_id) {
      toast.error('Bitte wählen Sie eine Abteilung');
      return;
    }
    
    setIsLoading(true);
    try {
      const cleanedData = {
        vorname: formData.vorname,
        nachname: formData.nachname,
        loginname: formData.loginname,
        email: formData.email || null,
        telefon: formData.telefon || null,
        dienstgrad: formData.dienstgrad || null,
        qualifikation: formData.qualifikation || null,
        abteilung_id: isLeitungsRolle ? null : formData.abteilung_id,
        rolle: formData.rolle,
        kategorien_ids: canAssignBenutzergruppen ? (formData.kategorien_ids || []) : [],
        tag_ids: formData.tag_ids || [],
        ist_aktiv: true,
        ist_gast: !!formData.ist_gast,
      };
      
      const response = await usersApi.create(cleanedData);
      setCreatedUser(response.data);
      onSuccess?.();
    } catch (error) {
      console.error('Create user error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler beim Erstellen';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setLoginnameEdited(false);
    setFormData({
      vorname: '',
      nachname: '',
      loginname: '',
      email: '',
      telefon: '',
      dienstgrad: '',
      qualifikation: '',
      abteilung_id: abteilungen[0]?.id || '',
      rolle: 'MITARBEITER',
      kategorien_ids: [],
      tag_ids: [],
    });
    setCreatedUser(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };

  if (createdUser) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <Check className="w-6 h-6" />
              Mitarbeiter erstellt!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center p-4 bg-muted/30 rounded-xl">
              <p className="text-lg font-semibold">
                {createdUser.vorname} {createdUser.nachname}
              </p>
              <p className="text-muted-foreground">{roleLabels[createdUser.rolle]}</p>
            </div>

            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-3">
                Zugangsdaten (bitte notieren!)
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between bg-white dark:bg-gray-900 p-3 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Loginname</p>
                    <p className="font-mono font-bold">{createdUser.loginname}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => copyToClipboard(createdUser.loginname)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex items-center justify-between bg-white dark:bg-gray-900 p-3 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Temporäres Passwort</p>
                    <p className="font-mono font-bold text-amber-600">{createdUser.temporaeres_passwort}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => copyToClipboard(createdUser.temporaeres_passwort)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                Der Mitarbeiter muss beim ersten Login das Passwort ändern.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleClose} className="w-full">
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Mitarbeiter hinzufügen</DialogTitle>
          <DialogDescription>
            Neuen Mitarbeiter zur Wache hinzufügen
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vorname *</Label>
              <Input
                value={formData.vorname}
                onChange={(e) => updateName('vorname', e.target.value)}
                placeholder="Max"
              />
            </div>
            <div className="space-y-2">
              <Label>Nachname *</Label>
              <Input
                value={formData.nachname}
                onChange={(e) => updateName('nachname', e.target.value)}
                placeholder="Mustermann"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Loginname *</Label>
            <Input
              value={formData.loginname}
              onChange={(e) => { setLoginnameEdited(true); setFormData(p => ({ ...p, loginname: e.target.value })); }}
              placeholder="mmustermann"
              minLength={3}
              maxLength={50}
              className="font-mono"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Dienstgrad</Label>
              <Select
                value={formData.dienstgrad || 'none'}
                onValueChange={(v) => setFormData(p => ({ ...p, dienstgrad: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Keiner —</SelectItem>
                  {dienstgradOptions.map(group => (
                    <React.Fragment key={group.group}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                        {group.group}
                      </div>
                      {group.options.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Qualifikation</Label>
              <Select
                value={formData.qualifikation || 'none'}
                onValueChange={(v) => setFormData(p => ({ ...p, qualifikation: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Keine —</SelectItem>
                  {qualifikationOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Hauptrolle</Label>
              <Select
                value={formData.rolle}
                onValueChange={(v) => setFormData(p => ({ ...p, rolle: v }))}
              >
                <SelectTrigger data-testid="create-rolle-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rolleOptions
                    .filter(opt => canManageRole(currentUserRolle, opt.value))
                    .map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            {/* Abteilung nur für nicht-Leitungsrollen. Wechsel nur ab WL-V. */}
            {formData.rolle !== 'WACHLEITUNG' && formData.rolle !== 'WACHLEITUNG_VERTRETUNG' && (
              <div className="space-y-2">
                <Label>
                  Abteilung *
                  {!abtEditable && (
                    <span className="ml-2 text-[10px] font-normal text-muted-foreground">
                      (eigene Wachabteilung)
                    </span>
                  )}
                </Label>
                <Select
                  value={formData.abteilung_id}
                  onValueChange={(v) => setFormData(p => ({ ...p, abteilung_id: v }))}
                  disabled={!abtEditable}
                >
                  <SelectTrigger data-testid="create-abteilung-select">
                    <SelectValue placeholder="Auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {abteilungen.map(abt => (
                      <SelectItem key={abt.id} value={abt.id}>{abt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {canAssignBenutzergruppen && availableKategorien.length > 0 && (
            <div className="space-y-2">
              <Label>Zusätzliche Benutzergruppen</Label>
              <div className="flex flex-wrap gap-2 rounded-lg border p-3">
                {availableKategorien.map((group) => {
                  const selected = (formData.kategorien_ids || []).includes(group.id);
                  return (
                    <Badge
                      key={group.id}
                      variant="outline"
                      className="cursor-pointer border-2"
                      style={selected
                        ? { backgroundColor: group.farbe, borderColor: group.farbe, color: 'white' }
                        : { borderColor: group.farbe, color: group.farbe }}
                      onClick={() => setFormData((current) => ({
                        ...current,
                        kategorien_ids: selected
                          ? current.kategorien_ids.filter((id) => id !== group.id)
                          : [...current.kategorien_ids, group.id],
                      }))}
                    >
                      {group.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {availableTags.length > 0 && (
            <div className="space-y-2">
              <Label>Tags und Qualifikationen</Label>
              <div className="flex flex-wrap gap-2 rounded-lg border p-3">
                {availableTags.map((tag) => {
                  const selected = (formData.tag_ids || []).includes(tag.id);
                  return (
                    <Badge
                      key={tag.id}
                      variant="outline"
                      className="cursor-pointer border-2"
                      style={selected
                        ? { backgroundColor: tag.farbe, borderColor: tag.farbe, color: 'white' }
                        : { borderColor: tag.farbe, color: tag.farbe }}
                      onClick={() => setFormData((current) => ({
                        ...current,
                        tag_ids: selected
                          ? current.tag_ids.filter((id) => id !== tag.id)
                          : [...current.tag_ids, tag.id],
                      }))}
                    >
                      {tag.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gast-Toggle */}
          <div className="space-y-2 p-3 rounded-lg border-2 border-dashed border-violet-300 bg-violet-50/30">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.ist_gast || false}
                onChange={(e) => setFormData(p => ({ ...p, ist_gast: e.target.checked }))}
                className="mt-0.5"
                data-testid="create-ist-gast-toggle"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-violet-900">Gast-Mitarbeiter</p>
                <p className="text-xs text-violet-700/80">
                  Wird im Gäste-Block der Einteilung angezeigt (nicht zur regulären Stammkraft zählend).
                </p>
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Wird erstellt...' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Abteilungs-Spalte
function DepartmentColumn({ abteilung, users, onUserClick, canDragDrop, canDragUser, availableTags }) {
  const columnId = abteilung.id;

  return (
    <div className="flex flex-col min-w-[352px] max-w-[420px] flex-1">
      {/* Column Header */}
      <div className="flex items-center justify-between px-3 py-2 mb-2">
        <div>
          <h3 className="font-bold text-sm">{abteilung.name}</h3>
          <p className="text-xs text-muted-foreground">{users.length} Mitarbeiter</p>
        </div>
      </div>

      {/* Cards Container - no individual scroll */}
      <Droppable droppableId={columnId} isDropDisabled={!canDragDrop}>
        {(provided, snapshot) => (
          <div 
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              'flex-1 p-2 space-y-2 rounded-xl min-h-[200px]',
              'bg-gradient-to-b from-muted/30 to-muted/10',
              snapshot.isDraggingOver && 'bg-primary/5 ring-2 ring-primary/20 ring-inset'
            )}
          >
            {users.map((user, index) => (
              <Draggable 
                key={user.id} 
                draggableId={user.id} 
                index={index}
                isDragDisabled={!canDragDrop || !canDragUser(user)}
                disableInteractiveElementBlocking
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                  >
                    <StaffCard 
                      dragHandleProps={provided.dragHandleProps}
                      user={user} 
                      onClick={() => onUserClick(user)}
                      isDragging={snapshot.isDragging}
                      availableTags={availableTags}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            
            {users.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Keine Mitarbeiter</p>
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

export function MitarbeiterPage() {
  const {
    user: currentUser,
    effectivePermissions,
    hasEffectivePermission,
    canSetZusatzberechtigungen: canSetZB,
  } = useAuthStore();
  const [abteilungen, setAbteilungen] = useState([]);
  const [usersByAbteilung, setUsersByAbteilung] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [availableKategorien, setAvailableKategorien] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [helmConfig, setHelmConfig] = useState(null);
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const canCreate = hasEffectivePermission('users.create');
  const canUpdateAllDepartments = canManageAllDepartments(
    currentUser,
    effectivePermissions,
    'update',
  );
  const canCreateAllDepartments = canManageAllDepartments(
    currentUser,
    effectivePermissions,
    'create',
  );
  const canDragDrop = hasEffectivePermission('users.update') && canUpdateAllDepartments;
  const canDragUser = (targetUser) => canDragDrop && canManageUserAction(
    currentUser,
    targetUser?.rolle,
    targetUser?.abteilung_id,
    effectivePermissions,
    'update',
  );
  const canAssignBenutzergruppen = hasEffectivePermission('users.groups.assign');
  const canSetZusatzberechtigungen = !!canSetZB?.();
  const canEditSelected = canManageUserAction(
    currentUser,
    selectedUser?.rolle,
    selectedUser?.abteilung_id,
    effectivePermissions,
    'update',
  );
  const canDeleteSelected = canManageUserAction(
    currentUser,
    selectedUser?.rolle,
    selectedUser?.abteilung_id,
    effectivePermissions,
    'delete',
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [abtRes, usersRes, tagsRes, permRes, fzRes, katRes, helmRes] = await Promise.all([
        wacheApi.getAbteilungen(),
        usersApi.getAll({ ist_aktiv: true }),
        tagsApi.getAll().catch(() => ({ data: [] })),
        permissionsApi.getAvailable().catch(() => ({ data: { permissions: [] } })),
        dienstApi.getFahrzeuge().catch(() => ({ data: [] })),
        kategorienApi.list().catch(() => ({ data: [] })),
        wacheApi.getHelme().catch(() => ({ data: null })),
      ]);

      setHelmConfig(helmRes.data);
      setAbteilungen(abtRes.data);
      setSelectedUser(previous => previous ? usersRes.data.find(person => person.id === previous.id) || previous : null);
      setAvailableTags(tagsRes.data || []);
      setAvailableKategorien(katRes.data || []);
      setAvailablePermissions(permRes.data?.permissions || []);
      setFahrzeuge(fzRes.data || []);
      
      // Separate users into groups:
      // - leitung: WACHLEITUNG and WACHLEITUNG_VERTRETUNG roles (shown in separate section)
      // - developer: DEVELOPER role (hidden from everyone)
      // - regular: all others (shown in department columns)
      const grouped = {};
      abtRes.data.forEach(abt => {
        grouped[abt.id] = [];
      });
      
      // Special groups
      grouped['_leitung'] = [];
      
      usersRes.data.forEach(user => {
        // Skip DEVELOPER users completely - they should not appear anywhere
        if (user.rolle === 'DEVELOPER') {
          return;
        }
        // WACHLEITUNG and WACHLEITUNG_VERTRETUNG go to special Leitung section
        if (user.rolle === 'WACHLEITUNG' || user.rolle === 'WACHLEITUNG_VERTRETUNG') {
          grouped['_leitung'].push(user);
          return;
        }
        // Regular users go to their department
        if (user.abteilung_id && grouped[user.abteilung_id]) {
          grouped[user.abteilung_id].push(user);
        }
      });
      setUsersByAbteilung(grouped);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !canDragDrop) return;
    
    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const newUsersByAbteilung = { ...usersByAbteilung };
    const sourceUsers = [...newUsersByAbteilung[source.droppableId]];
    const destUsers = source.droppableId === destination.droppableId 
      ? sourceUsers 
      : [...newUsersByAbteilung[destination.droppableId]];

    const [movedUser] = sourceUsers.splice(source.index, 1);
    if (!canDragUser(movedUser)) return;
    destUsers.splice(destination.index, 0, { ...movedUser, abteilung_id: destination.droppableId });

    newUsersByAbteilung[source.droppableId] = sourceUsers;
    newUsersByAbteilung[destination.droppableId] = destUsers;
    setUsersByAbteilung(newUsersByAbteilung);

    if (source.droppableId !== destination.droppableId) {
      try {
        await usersApi.update(draggableId, { abteilung_id: destination.droppableId });
        toast.success('Abteilung aktualisiert');
      } catch (error) {
        console.error('Update error:', error);
        toast.error('Aktualisierung fehlgeschlagen');
        loadData();
      }
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setDetailDialogOpen(true);
  };

  const filteredUsersByAbteilung = {};
  Object.entries(usersByAbteilung).forEach(([abtId, users]) => {
    filteredUsersByAbteilung[abtId] = users
      .filter(u =>
        `${u.vorname} ${u.nachname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.loginname?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort(compareByDienstgrad);
  });

  // Count only regular users (not leitung)
  const totalUsers = Object.entries(usersByAbteilung)
    .filter(([key]) => key !== '_leitung')
    .reduce((sum, [, users]) => sum + users.length, 0);
  
  const leitungUsers = filteredUsersByAbteilung['_leitung'] || [];

  return (
    <MainLayout>
      <div className="min-h-full" data-testid="staff-page">
        {/* Header - sticky */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 -mx-4 px-4 pt-4 -mt-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold">Mitarbeiter</h1>
              <p className="text-sm text-muted-foreground">
                {totalUsers} Mitarbeiter in {abteilungen.length} Abteilungen
                {leitungUsers.length > 0 && ` + ${leitungUsers.length} Leitung`}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48 sm:w-64 h-9"
                />
              </div>
              
              {canCreate && (
                <Button onClick={() => setAddDialogOpen(true)} data-testid="add-staff-btn" size="sm">
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Hinzufügen</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Leitung Section - only visible if there are Wachleitung users */}
        {leitungUsers.length > 0 && (
          <section className="mb-6 space-y-3 rounded-xl bg-white p-5 shadow-[0_2px_6px_rgba(15,23,42,0.04),0_8px_24px_rgba(15,23,42,0.08)] dark:bg-slate-800 dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)]" aria-labelledby="wachleitung-heading">
            <div>
              <h2 id="wachleitung-heading" className="text-base font-semibold tracking-tight">Wachleitung</h2>
              <p className="mt-1 text-xs text-muted-foreground">Zentrale Führung</p>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,336px),1fr))] gap-3">
              {leitungUsers.map(user => (
                <StaffCard
                  key={user.id}
                  user={user}
                  onClick={() => handleUserClick(user)}
                  availableTags={availableTags}
                  roleLabel={roleLabels[user.rolle] || user.rolle}
                />
              ))}
            </div>
          </section>
        )}

        {/* Board */}
        {isLoading ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="min-w-[260px] flex-1">
                <Skeleton className="h-64 rounded-xl" />
              </div>
            ))}
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex gap-3 overflow-x-auto pb-4">
              {abteilungen.map(abt => (
                <DepartmentColumn
                  key={abt.id}
                  abteilung={abt}
                  users={filteredUsersByAbteilung[abt.id] || []}
                  onUserClick={handleUserClick}
                  canDragDrop={canDragDrop}
                  canDragUser={canDragUser}
                  availableTags={availableTags}
                />
              ))}
            </div>
          </DragDropContext>
        )}

        {/* Dialogs */}
        <UserDetailDialog
          helmConfig={helmConfig}
          user={selectedUser}
          open={detailDialogOpen}
          onOpenChange={setDetailDialogOpen}
          abteilungen={abteilungen}
          onUpdate={loadData}
          canEdit={canEditSelected}
          isSelf={selectedUser?.id === currentUser?.id}
          canUpdateAllDepartments={canUpdateAllDepartments}
          canDelete={canDeleteSelected}
          availableTags={availableTags}
          availableKategorien={availableKategorien}
          canSetZusatzberechtigungen={canSetZusatzberechtigungen && canEditSelected}
          canAssignBenutzergruppen={canAssignBenutzergruppen && canEditSelected}
          availablePermissions={availablePermissions}
          currentUserRolle={currentUser?.rolle}
          fahrzeuge={fahrzeuge}
        />
        
        <AddUserDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          abteilungen={abteilungen}
          onSuccess={loadData}
          currentUserRolle={currentUser?.rolle}
          currentUserAbteilungId={currentUser?.abteilung_id}
          canCreateAllDepartments={canCreateAllDepartments}
          canAssignBenutzergruppen={canAssignBenutzergruppen}
          availableKategorien={availableKategorien}
          availableTags={availableTags}
        />
      </div>
    </MainLayout>
  );
}

export default MitarbeiterPage;
